<template>
    <div>
        <template v-if="enabled">
            <label style="margin-left: 10px; font-weight: bold">{{ $t('Filter') }}</label>
            <div class="search-input" style="margin-top: 5px">
                <cic-textbox id="txtSearch" :placeholder="$t('Search')" :value="search" @input="handleSearch" />
                <cic-button v-if="search" id="btnClear" @click="handleSearch(null)">
                    {{ $t('Clear Filter') }}
                </cic-button>
            </div>
            <hr />
        </template>
        <table class="all-objects-table">
            <tbody>
                <tr>
                    <td style="width: 60px">
                        <cic-checkbox :value="value.all" :enabled="enabled" @click="handleAllClick" />
                    </td>
                    <td style="font-weight: normal">
                        {{ $t('All') }}
                    </td>
                </tr>
            </tbody>
        </table>
        <hr />
        <div v-if="enabled && !value.all" class="select-options">
            <cic-button id="btnSelectAll" @click="selectAll">
                {{ search ? $t('Select All Search Results') : $t('Select All') }}
            </cic-button>
            <cic-button id="btnDeselectAll" @click="deselectAll">
                {{ search ? $t('Deselect All Search Results') : $t('Deselect All') }}
            </cic-button>
        </div>
        <div :style="{ maxHeight: totalPages > 1 ? '216px' : '270px', overflowY: 'auto' }">
            <table class="objects-table">
                <tbody>
                    <tr>
                        <td style="width: 60px">
                            {{ $t('Visible?') }}
                        </td>
                        <td v-for="column in type.columnLabels.split(',')">
                            {{ $t(column) }}
                        </td>
                    </tr>
                    <tr v-for="row in filtered">
                        <td>
                            <cic-checkbox
                                :value="row.allowed"
                                :enabled="enabled && !value.all"
                                @click="(val) => handleClick(row.id, val)"
                            />
                        </td>
                        <td v-for="column in type.columns.split(',')" style="font-weight: normal">
                            {{ getRowLabel(row, column) }}
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
        <ul v-if="totalPages > 1">
            <li class="cic-footer" style="border: none">
                <cic-page-control
                    :page="page"
                    :total-rows="results.length"
                    :results-per-page="rowsPerPage"
                    @change="(val) => (page = val)"
                />
            </li>
        </ul>
    </div>
</template>

<script>
    import CicButton from '@/components/CicButton';
    import CicPageControl from '@/components/CicPageControl';
    import CicCheckbox from '@/components/inputs/CicCheckbox';
    import CicTextbox from '@/components/inputs/CicTextbox';

    export default {
        name: 'EditObjects',
        components: {
            CicPageControl,
            CicButton,
            CicCheckbox,
            CicTextbox,
        },
        props: {
            value: {
                type: Object,
                required: true,
            },
            enabled: {
                type: Boolean,
                default: false,
            },
            type: {
                type: Object,
                required: true,
            },
        },
        emits: ['input'],
        data() {
            return {
                page: 1,
                rowsPerPage: 100,
                search: null,
                tempPage: 1,
            };
        },
        computed: {
            filtered() {
                return this.results.filter(
                    (x, index) => index >= (this.page - 1) * this.rowsPerPage && index < this.page * this.rowsPerPage,
                );
            },
            results() {
                if (this.search) {
                    return this.value.data.filter((x) => {
                        return !!this.type.columns.split(',').find((y) => {
                            return !!this.getRowLabel(x, y).match(new RegExp(this.searchString, 'i'));
                        });
                    });
                }

                return this.value.data;
            },
            searchString() {
                return this.search.replace(/[[\]{}()+?.\\^$|]/g, '\\$&').replace('*', '.*');
            },
            totalPages() {
                return this.results.length > 1 ? Math.ceil(this.results.length / this.rowsPerPage) : 1;
            },
        },
        methods: {
            deselectAll() {
                let data = JSON.parse(JSON.stringify(this.value));
                this.results
                    .filter((x) => x.allowed)
                    .forEach((x) => {
                        data.data.find((y) => y.id === x.id).allowed = false;
                    });
                this.$emit('input', data);
            },

            getRowLabel(row, column) {
                let value = '';
                if (column.indexOf(';') === -1) {
                    value = row[column.trim()] === null ? '' : row[column.trim()];
                    if (Array.isArray(value)) {
                        if (this.search) {
                            let matches = value.filter((x) => !!x.match(new RegExp(this.searchString, 'i')));
                            if (matches.length) {
                                value = matches;
                            }
                        }

                        value = value.join(', ');
                    }
                    if (value.length > 100) {
                        value = value.substring(0, 100) + '...';
                    }
                } else {
                    let parts = column.split(';');
                    value = row[parts[0].trim()];
                    let values = parts.filter(
                        (x, index) => index > 0 && x.substring(0, x.indexOf('=')).trim() === value.toString(),
                    );
                    if (values.length) {
                        value = this.$t(values[0].substring(values[0].indexOf('=') + 1).trim());
                    }
                }

                return value;
            },

            handleAllClick(val) {
                let data = JSON.parse(JSON.stringify(this.value));
                data.all = val;
                if (val) {
                    data.data
                        .filter((x) => !x.allowed)
                        .forEach((x) => {
                            x.allowed = true;
                        });
                }
                this.$emit('input', data);
            },

            handleClick(id, val) {
                if (!this.value.all) {
                    let data = JSON.parse(JSON.stringify(this.value));
                    data.data.find((x) => x.id === id).allowed = val;
                    this.$emit('input', data);
                }
            },

            handleSearch(search) {
                this.page = 1;
                this.search = search;
            },

            selectAll() {
                let data = JSON.parse(JSON.stringify(this.value));
                this.results
                    .filter((x) => !x.allowed)
                    .forEach((x) => {
                        data.data.find((y) => y.id === x.id).allowed = true;
                    });
                this.$emit('input', data);
            },
        },
    };
</script>
